import { create } from 'zustand';

/** Generic store for plugin data arriving over WebSocket */
interface PluginDataState {
  /** Plugin data keyed by WS event type (e.g. 'space:satellites', 'patrol:units') */
  data: Map<string, unknown[]>;

  /** Update data for a specific event type */
  setData: (eventType: string, payload: unknown[]) => void;

  /** Clear all plugin data */
  clear: () => void;
}

/**
 * Memory bound per event type — snapshots larger than this are truncated so a
 * runaway feed can't grow the store unbounded (dense layers are additionally
 * viewport-culled at render time).
 */
const MAX_ENTRIES_PER_EVENT = 5_000;

/** Store for plugin-specific WS data that doesn't have a dedicated store */
export const usePluginDataStore = create<PluginDataState>((set) => ({
  data: new Map(),

  setData: (eventType, payload) =>
    set((state) => {
      const next = new Map(state.data);
      next.set(
        eventType,
        payload.length > MAX_ENTRIES_PER_EVENT ? payload.slice(0, MAX_ENTRIES_PER_EVENT) : payload,
      );
      return { data: next };
    }),

  clear: () => set({ data: new Map() }),
}));

/** Hook to select data for a specific plugin event type */
export function usePluginData<T = unknown>(eventType: string): T[] {
  return usePluginDataStore((state) => (state.data.get(eventType) as T[]) ?? []);
}
